/**
 * RETRY WINDOWS (#4630): lets the network-error hook know that a failed request is about to be retried.
 *
 * The hook in consoleAndNetworkCapture.ts sits at the `fetch` layer, BELOW withTransientRetry. A 503 that the retry then
 * recovers from therefore produced the same Sentry error as one the technician actually hit, so Sentry could not say
 * whether anyone was blocked. A retry wrapper opens a window for the endpoint it calls; while a LATER attempt remains,
 * the hook records a breadcrumb instead of an error. The LAST attempt is never deferred, so a retry budget that runs out
 * still produces exactly one error (the failure the person saw), and an unwrapped caller is untouched.
 *
 * A deferred failure is HELD, not dropped: if the loop then gives up without a later attempt (a non-transient error, such as
 * a raw-fetch 500, is thrown at once), `abandon()` hands the held failure back to the reporter so it still becomes an error.
 *
 * The key is the callable name for our own callables, otherwise the URL without its query string; the same function
 * (`retryWindowKey`) is used on both sides so they cannot drift apart.
 */
import { addBreadcrumb } from './breadcrumbs';

const OUR_CALLABLE_URL = /^https?:\/\/[a-z0-9-]+-quoter-saas\.cloudfunctions\.net\/([A-Za-z0-9_]+)(?:[?#/]|$)/i;

export interface DeferredFailure {
  readonly url: string;
  readonly status: number;
  readonly method: string;
  readonly duration: number;
  readonly responseBody?: string;
}

interface RetryWindow {
  readonly key: string;
  readonly maxAttempts: number;
  readonly openedAt: number;
  attempt: number;
  /** True only while an attempt's request is in flight; false during the backoff sleep. */
  inFlight: boolean;
  /** The most recent failure held back; replayed if the loop ends in failure, dropped when an attempt succeeds. */
  held: DeferredFailure | null;
}

/** A window that has not closed after this long belongs to a hung operation; it stops deferring so it cannot blind the endpoint. */
const WINDOW_MAX_AGE_MS = 2 * 60 * 1000;

/** The normal reporter (handleNetworkError), registered by its module so this file does not import it (no cycle). */
let reportDeferred: ((failure: DeferredFailure) => void) | null = null;
let replaying = false;

export function setDeferredFailureReporter(reporter: (failure: DeferredFailure) => void): void {
  reportDeferred = reporter;
}

/** The key for an endpoint, from either a full URL or an already-bare callable name. */
export function retryWindowKey(urlOrName: string): string {
  const callable = OUR_CALLABLE_URL.exec(urlOrName);
  if (callable) return callable[1];
  return urlOrName.split(/[?#]/, 1)[0];
}

export interface RetryWindowHandle {
  /** Call at the start of each attempt, 0-based. */
  beginAttempt(attempt: number): void;
  /** Call when an attempt has failed and the loop is about to sleep before the next one. */
  endAttempt(): void;
  /** Call when an attempt succeeded: the held failure (if any) was recovered. */
  recovered(): void;
  /** Call when the loop throws WITHOUT another attempt: reports the failure this window held back. Then close(). */
  abandon(): void;
  /** Call once when the retry loop ends, success or failure. */
  close(): void;
}

const open = new Set<RetryWindow>();

export function openRetryWindow(urlOrName: string, maxAttempts: number): RetryWindowHandle {
  const win: RetryWindow = { key: retryWindowKey(urlOrName), maxAttempts, openedAt: Date.now(), attempt: 0, inFlight: false, held: null };
  open.add(win);
  return {
    beginAttempt(attempt) {
      win.attempt = attempt;
      win.inFlight = true;
    },
    endAttempt() {
      win.inFlight = false;
    },
    recovered() {
      win.inFlight = false;
      win.held = null;
    },
    abandon() {
      const held = win.held;
      win.held = null;
      win.inFlight = false;
      open.delete(win);
      if (!held || !reportDeferred) return;
      replaying = true;
      try {
        reportDeferred(held);
      } finally {
        replaying = false;
      }
    },
    close() {
      open.delete(win);
    },
  };
}

/**
 * True when `url` failed inside the ONE open retry window for its endpoint, while that window's request was in flight and a
 * later attempt remains, so this failure is not yet the person's failure. The failure is held, and a breadcrumb records it.
 *
 * Fails open (returns false, so the failure reports as before) whenever the failure cannot be pinned to a single retrying
 * call: two windows on one endpoint, a window between attempts, a window past its age cap, or the last attempt. A failure
 * on the last attempt also supersedes anything the window held (it is reported itself, so no replay doubles it).
 */
export function deferToRetry(url: string, status: number, method = 'GET', duration = 0, responseBody?: string): boolean {
  if (replaying) return false;
  const key = retryWindowKey(url);
  const matching = Array.from(open).filter((w) => w.key === key);
  if (matching.length === 0) return false;
  if (matching.length > 1) return false;
  const win = matching[0];
  if (!win.inFlight || Date.now() - win.openedAt > WINDOW_MAX_AGE_MS) return false;
  if (win.attempt >= win.maxAttempts - 1) {
    win.held = null;
    return false;
  }
  win.held = { url, status, method, duration, responseBody };
  addBreadcrumb('console', 'network_error_retrying', `HTTP ${status}: ${key}`, {
    status,
    attempt: win.attempt + 1,
    of: win.maxAttempts,
  });
  return true;
}

/** Breadcrumb for a call that failed transiently and then succeeded: the "recovered" half of the signal. */
export function noteRecovered(urlOrName: string, attempts: number): void {
  addBreadcrumb('console', 'network_error_recovered', retryWindowKey(urlOrName), { attempts });
}

export function __resetRetryWindowsForTests(): void {
  open.clear();
}
